import { formatISO, parse } from 'date-fns'
import { v4 as uuidv4 } from 'uuid'
import { DEFAULT_ORDER_ID } from '../constants'
import { assembleCharges } from './assembleCharges'
import { assembleLotMatchingInstructions } from './assembleLotMatchingInstructions'
import { assembleSpecialInstructions } from './assembleSpecialInstructions'

import {
  InstrumentTypeEnum,
  OpenCloseEnum,
  ShortTypeEnum,
  SideTypeEnum,
  SubmissionPayload,
  TradeActionEnum,
  TradeFormValues,
  TradeTypeEnum,
} from '../Types'

export const formatPayload = (values: TradeFormValues, correspondentCode: string, accountNumber: string) => {
  const payload: SubmissionPayload = {
    ...assembleCharges(values),
    correspondent: correspondentCode,
    externalId: `AO${uuidv4()}`,
    instrument: {
      instrumentId: {
        id: values.symbol,
        listingCountry: values.listingCountry,
        type: values.securityCodeType,
      },
      type: values.instrumentType,
    },
    reporting: {
      ...(values.memo && { memo: values.memo }),
      ...(values.repCode && { repCode: values.repCode }),
      ...assembleSpecialInstructions(values.specialInstructions),
    },
    settlementInstructions: {
      ...(values.settlementDate && {
        settlementDate: formatISO(parse(values.settlementDate, 'MM/dd/yyyy', new Date()), {
          representation: 'date',
        }),
      }),
    },
    transaction: {
      accountNumber,
      accountType: values.accountType,
      brokerCapacity: values.capacity,
      currency: values.currency,
      ...assembleLotMatchingInstructions(values.lotMatchingInstructions),
      ...(values.routeType && {
        route: {
          type: values.routeType,
          ...(values.executionAlgorithm && { algorithmType: values.executionAlgorithm }),
        },
      }),
      price: Number(values.executionPrice),
      quantity: Number(values.quantity),
      side: {
        ...(isShort(values) && { shortType: getShortType(values) }),
        type: getSideType(values),
      },
      ...(values.instrumentType === InstrumentTypeEnum.option && { openClose: getOpenCloseValue(values) }),
      transactionDateTime: values.tradeTime
        ? concatDateAndTime(values.tradeDate, values.tradeTime)
        : new Date(values.tradeDate).toISOString(),
      type: values.tradeType === TradeTypeEnum.allocation ? TradeTypeEnum.allocation : TradeTypeEnum.execution,
      ...(values.market && { exchange: values.market }),
      ...(values.executingBroker && { executingBroker: values.executingBroker }),
      orderId: values.orderId?.trim() || DEFAULT_ORDER_ID,
      ...(values.sourceAccountNumber && { sourceAccountNumber: values.sourceAccountNumber }), //Allocation type only
      ...(values.sourceAccountType && { sourceAccountType: values.sourceAccountType }), //Allocation type only
    },
    version: 2,
  }
  return payload
}

const isShort = (values: TradeFormValues) =>
  values.action === TradeActionEnum.BuyToCover || values.action === TradeActionEnum.ShortSell // if it is a short then this value is not defined and not sent to the server

const getOpenCloseValue = (values: TradeFormValues) =>
  // @ts-ignore
  ({
    [TradeActionEnum.BuyToClose]: OpenCloseEnum.Close,
    [TradeActionEnum.BuyToOpen]: OpenCloseEnum.Open,
    [TradeActionEnum.SellToClose]: OpenCloseEnum.Close,
    [TradeActionEnum.SellToOpen]: OpenCloseEnum.Open,
  }[values.action!])

const getShortType = (values: TradeFormValues) =>
  values.action === TradeActionEnum.BuyToCover ? ShortTypeEnum.Cover : ShortTypeEnum.Short

const getSideType = (values: TradeFormValues) =>
  ({
    [TradeActionEnum.Buy]: SideTypeEnum.Buy,
    [TradeActionEnum.BuyToClose]: SideTypeEnum.Buy,
    [TradeActionEnum.BuyToCover]: SideTypeEnum.Buy,
    [TradeActionEnum.BuyToOpen]: SideTypeEnum.Buy,
    [TradeActionEnum.Sell]: SideTypeEnum.Sell,
    [TradeActionEnum.SellToClose]: SideTypeEnum.Sell,
    [TradeActionEnum.SellToOpen]: SideTypeEnum.Sell,
    [TradeActionEnum.ShortSell]: SideTypeEnum.Sell,
  }[values.action!])

const concatDateAndTime = (tradeDate: string, tradeTime: string) => {
  const date = `${tradeDate} ${tradeTime.split(' ')[0]}`
  return new Date(date).toISOString()
}
